/* eslint-disable import/no-cycle */
import React, {useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faExclamationTriangle} from '@fortawesome/pro-solid-svg-icons';
import {Form} from 'antd';
import {take} from 'lodash';
import {WidgetLayouts} from '../shared/widgets/WidgetLayouts';
import {WidgetLayout} from '../../models/widgetLayout';
import OperatorWidget from '../operator/shared/OperatorWidget';
import {useStore} from '../../hooks/useStore';
import {InterruptionWidgetConfig} from './interruptionWidgetConfig';
import {Timer} from './Timer';
import Spinner from '../shared/spinners/Spinner';
import {RecordButton} from './RecordButton';
import {useMount} from '../../hooks/useMount';
import InterruptionModal from './InterruptionModal';
import {StopButton} from './StopButton';
import {ScopeContext} from '../../policies/scopeContext';
import {transition, workflow} from '../../models/scope';
import QuickStopButtons from './partials/QuickStopButtons';
import {useModulePolicy} from '../../hooks/useModulePolicy';

const InterruptionWidget = ({disabled, minimized = false, identifier}) => {
  const store = useStore();
  const {t} = useTranslation();
  const widgetConfig = new InterruptionWidgetConfig(store, identifier);
  const [form] = Form.useForm();
  const scopes = [transition];
  const policy = useModulePolicy(store, scopes);

  const [modalVisible, setModalVisible] = useState(false);
  const [currentInterruptionLog, setCurrentInterruptionLog] = useState(null);

  const quickstops = take(
    store.interruptionReasonStore.quickstopInterruptionReasons,
    widgetConfig.getValues()?.maxAmountQuickstopsToDisplay || 0
  );

  useMount(() => {
    const latestWorkplaceLog = store.workplaceStateLogStore.latestBySelectedWorkplace;
    if (latestWorkplaceLog) {
      const latestOperationLog = store.operationStateLogStore.latestBySelectedWorkplace;

      const currentWorkplaceState = latestWorkplaceLog
        ? store.workplaceStateStore.getById(latestWorkplaceLog.stateId)
        : undefined;
      const activeOperationState = latestOperationLog
        ? store.operationStateStore.getById(latestOperationLog.stateId)
        : undefined;
      let latestLog = latestWorkplaceLog;

      if (latestOperationLog && latestOperationLog.start > latestWorkplaceLog.start) {
        latestLog = latestOperationLog;
      }

      const {start, message, interruptionReasonId} = latestLog;

      if (currentWorkplaceState?.isInterruption || activeOperationState?.isInterruption) {
        setCurrentInterruptionLog({start, message, interruptionReasonId});
      } else {
        setCurrentInterruptionLog(null);
      }
    }
  }, [
    store.workplaceStore.selectedWorkplace?.id,
    JSON.stringify(store.validTransitionsStore.transitions.map((trans) => trans.id)),
    store.operationStateLogStore.latestBySelectedWorkplace,
    store.operationStateLogStore.latestBySelectedWorkplace?.interruptionReasonId,
    store.workplaceStateLogStore.latestBySelectedWorkplace,
    store.workplaceStateLogStore.latestBySelectedWorkplace?.interruptionReasonId,
  ]);

  useMount(() => {
    if (!store.validTransitionsStore.isLoadingCollection && store.workplaceStore.selectedWorkplace) {
      store.validTransitionsStore.loadValidTransitionsByWorkplace(
        store.workplaceStore.selectedWorkplace.id
      );
    }
  }, [store.workplaceStore.selectedWorkplace?.id]);

  const handleSubmit = ({transitionId, ...values}) => {
    store.transitionStore.performTransition({
      workplaceId: store.workplaceStore.selectedWorkplace?.id,
      body: {
        transitionId,
        activeOperationId: store.operationStore.active?.id,
        timestamp: values.datetime ? values.datetime.milliseconds(0).toISOString() : undefined,
        interruptionReasonId: values.reason,
        message: values.comment || undefined,
      },
    }).finally(() => {
      setModalVisible(false);
      store.transitionStore.performedTransition += 1;
    });
  };

  const isLoading = () => store.operationStore.isLoadingCollection
    || store.operationStateLogStore.isLoadingCollection
    || store.workplaceStateLogStore.isLoadingCollection;

  const onInterruptionModalCancel = () => {
    form.resetFields();
    setModalVisible(false);
  };

  return (
    <ScopeContext.Provider value={[workflow]}>
      <OperatorWidget
        icon={<FontAwesomeIcon icon={faExclamationTriangle}/>}
        title={t('interruption.widget.title')}
        disabled={disabled}
        minimized={minimized}
        identifier={identifier}
        isPinable
        widgetConfig={widgetConfig}
      >
        {isLoading() && <Spinner fullWidth fullHeight/>}
        {!isLoading() && currentInterruptionLog && (
          <>
            <StopButton disabled={!policy.canExecute()} onClick={() => setModalVisible(true)} size="large"/>
            <Timer time={currentInterruptionLog.start}/>
          </>
        )}
        {!isLoading() && !currentInterruptionLog && store.validTransitionsStore.hasValidInterruptionTransitions && (
          <>
            <RecordButton onClick={() => setModalVisible(true)} size="large" disabled={!policy.canExecute()}/>
            <QuickStopButtons
              quickstops={quickstops}
              disabled={!policy.canExecute()}
            />
          </>
        )}
      </OperatorWidget>
      {modalVisible && (
        <InterruptionModal
          title={(
            <>
              <FontAwesomeIcon icon={faExclamationTriangle}/>
              {' '}
              {currentInterruptionLog ? t('interruption.modal.title.active') : t('interruption.modal.title.inactive')}
            </>
          )}
          onCancel={onInterruptionModalCancel}
          onSubmit={handleSubmit}
          form={form}
          isInterruption={currentInterruptionLog}
          currentLog={currentInterruptionLog}
        />
      )}
    </ScopeContext.Provider>
  );
};

InterruptionWidget.icon = faExclamationTriangle;
InterruptionWidget.isPinable = true;

InterruptionWidget.identifier = 'InterruptionWidget';
InterruptionWidget.defaultLayout = new WidgetLayout(
  {
    identifier: InterruptionWidget.identifier,
    x: 2,
    y: 8,
    height: 3,
    minHeight: 3,
    width: WidgetLayouts.sixthWidth.w,
    minWidth: WidgetLayouts.sixthWidth.minW,
  }
);

export default observer(InterruptionWidget);
